<template>
    <AdminLayout :crumbs="[{ label: season_label_plural }]">
        <div class="a-page-head">
            <div>
                <h1 class="a-page-title">{{ season_label_plural }}</h1>
                <p class="a-page-subtitle">Cada {{ season_label.toLowerCase() }} agrupa sus etapas, sus rankings y los equipos inscritos.</p>
            </div>
            <button v-if="is_admin" type="button" class="btn btn-primary" @click="open_create"><i class="bi bi-plus-lg"></i>Nuevo {{ season_label.toLowerCase() }}</button>
        </div>

        <div class="a-card">
            <div class="a-card-header" v-if="seasons.length > 5">
                <div class="a-search flex-fill">
                    <i class="bi bi-search"></i>
                    <input type="search" class="form-control" v-model="search" :placeholder="'Buscar ' + season_label.toLowerCase()">
                </div>
            </div>
            <ul class="a-list" v-if="filtered.length">
                <li v-for="ev in filtered" :key="ev.id" class="a-row a-row-link" @click="open(ev)">
                    <div class="ev-icon"><component :is="theme_of(ev).rider" color="#00a7af" :size="34" /></div>
                    <div class="a-row-main">
                        <div class="d-flex align-items-center gap-2">
                            <span class="a-row-title">{{ ev.name }}</span>
                            <span v-if="live_season_id == ev.id" class="a-pill a-pill-live">En directo</span>
                            <span v-else class="a-pill" :class="status(ev).cls">{{ status(ev).label }}</span>
                        </div>
                        <div class="a-row-meta">
                            <span><i class="bi bi-calendar3"></i>{{ range(ev) }}</span>
                            <span><i class="bi bi-signpost-split"></i>{{ plural(count_stages(ev), 'etapa') }}</span>
                            <span><i class="bi bi-people"></i>{{ plural(count_participants(ev), 'equipo') }}</span>
                            <span v-if="ev.join_code"><i class="bi bi-key"></i><span class="a-mono">{{ ev.join_code }}</span></span>
                        </div>
                    </div>
                    <div class="a-row-actions" @click.stop>
                        <button type="button" class="btn btn-secondary btn-sm" @click="$refs.qr.open(ev)"><i class="bi bi-qr-code"></i>Acceso</button>
                        <div class="dropdown" v-if="is_admin">
                            <button type="button" class="btn btn-ghost btn-icon" title="Más acciones" data-bs-toggle="dropdown" aria-expanded="false"><i class="bi bi-three-dots"></i></button>
                            <ul class="dropdown-menu dropdown-menu-end">
                                <li><router-link class="dropdown-item" :to="{ name: 'EventSettings', params: { season_id: ev.id } }"><i class="bi bi-gear"></i>Configuración</router-link></li>
                                <li><a class="dropdown-item" href="javascript:void(0)" @click="ask_reset(ev)"><i class="bi bi-arrow-counterclockwise"></i>Reiniciar evento</a></li>
                                <li><hr class="dropdown-divider"></li>
                                <li><a class="dropdown-item text-danger" href="javascript:void(0)" @click="ask_delete(ev)"><i class="bi bi-trash3"></i>Eliminar</a></li>
                            </ul>
                        </div>
                        <i class="bi bi-chevron-right a-muted"></i>
                    </div>
                </li>
            </ul>
            <div v-else class="a-empty">
                <i class="bi bi-calendar-plus"></i>
                <div class="a-empty-title">{{ search ? 'Nada coincide con la búsqueda' : 'Todavía no hay ' + season_label_plural.toLowerCase() }}</div>
                <div v-if="!search">Crea uno para empezar a preparar sus etapas.</div>
            </div>
        </div>

        <!-- New event -->
        <div class="modal fade a-modal" ref="createModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content" v-if="draft">
                    <div class="modal-header">
                        <h1 class="modal-title">Nuevo {{ season_label.toLowerCase() }}</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">
                        <SeasonFields :model="draft" />
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" class="btn btn-primary" @click="create">Crear</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delete event -->
        <div class="modal fade a-modal" ref="deleteModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content" v-if="to_delete">
                    <div class="modal-header">
                        <h1 class="modal-title">Eliminar {{ to_delete.name }}</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">
                        Se eliminarán también sus <b>{{ plural(count_stages(to_delete), 'etapa') }}</b>, sus rankings y las inscripciones de sus equipos. No se puede deshacer.
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" class="btn btn-danger" @click="remove">Eliminar</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Reset event: wipe what was played, keep how it's set up -->
        <div class="modal fade a-modal" ref="resetModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content" v-if="to_reset">
                    <div class="modal-header">
                        <h1 class="modal-title">Reiniciar {{ to_reset.name }}</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">
                        <p>Se para lo que esté en marcha, los equipos vuelven a la sala de espera y se <b>borran todas sus respuestas</b> en este {{ season_label.toLowerCase() }}, junto con las marcas de preguntas jugadas y etapas iniciadas o finalizadas.</p>
                        <p class="mb-0">Se conservan los equipos inscritos, las etapas, las preguntas y la configuración. No se puede deshacer.</p>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" class="btn btn-danger" @click="reset">Reiniciar</button>
                    </div>
                </div>
            </div>
        </div>

        <EventQrModal ref="qr" />
    </AdminLayout>
</template>

<script>
import { Modal } from 'bootstrap'
import utils from '@/../../shared/utils'
import AdminLayout from '@/components/admin/AdminLayout.vue'
import EventQrModal from '@/components/admin/EventQrModal.vue'
import SeasonFields from '@/components/admin/SeasonFields.vue'
import { season_status, format_range, plural, with_iso_dates } from '@/utils/admin-format'
import { get_theme } from '@/themes'

// Admin home: every event at a glance. Opening one goes to its workspace.
export default {
    name: 'EventsHome',
    components: { AdminLayout, EventQrModal, SeasonFields },
    data(){
        return { search: '', draft: null, to_delete: null, to_reset: null };
    },
    computed: {
        season_label(){ return utils.get_season_label(window.MODE); },
        season_label_plural(){ return utils.get_season_label(window.MODE, true); },
        is_admin(){
            const user = this.$store.state.game.user;
            return !!user && user.role == utils.USER_ROLES.ADMINISTRATOR;
        },
        seasons(){ return this.$store.state.game.seasons || []; },
        filtered(){
            const term = this.search.trim().toLowerCase();
            return this.seasons.filter((s) => (s.name || '').toLowerCase().includes(term));
        },
        live_season_id(){
            const game = this.$store.state.game;
            const live = game.stage_playback || (game.state && ![utils.GAME_STATES.IDLE, utils.GAME_STATES.AUTH].includes(game.state));
            return live ? game.active_season_id : null;
        }
    },
    created(){
        // Old "Panel?stage_id=" links (pre-redesign) land on the stage itself.
        const stage_id = this.$route.query.stage_id;
        if(utils.is_valid_parameter(stage_id)){
            const stage = (this.$store.state.game.stages || []).find((s) => s.id == stage_id);
            if(stage) this.$router.replace({ name: 'StageRundown', params: { season_id: stage.season_id, stage_id: stage.id } });
        }
    },
    mounted(){
        this.create_modal = new Modal(this.$refs.createModal);
        this.delete_modal = new Modal(this.$refs.deleteModal);
        this.reset_modal = new Modal(this.$refs.resetModal);
    },
    beforeUnmount(){
        this.create_modal.dispose();
        this.delete_modal.dispose();
        this.reset_modal.dispose();
    },
    methods: {
        status: season_status,
        plural,
        range(ev){ return format_range(ev.start_date, ev.end_date); },
        theme_of(ev){ return get_theme(ev.theme); },
        count_stages(ev){ return (this.$store.state.game.stages || []).filter((s) => s.season_id == ev.id).length; },
        count_participants(ev){ return (this.$store.state.game.season_users || []).filter((su) => su.season_id == ev.id).length; },
        open(ev){
            this.$router.push({ name: 'Stages', params: { season_id: ev.id } });
        },
        open_create(){
            this.draft = { name: '', pre_start_date: null, start_date: null, end_date: null, base_score: 10, theme: utils.DEFAULT_THEME };
            this.create_modal.show();
        },
        create(){
            if(!utils.is_valid_parameter(this.draft.name)){
                window.toast.error('Ponle un nombre.');
                return;
            }
            window.network.wso.send(JSON.stringify({ action: window.RPC.REGISTER_SEASON, params: with_iso_dates(this.draft) }));
            this.create_modal.hide();
        },
        ask_delete(ev){
            this.to_delete = ev;
            this.delete_modal.show();
        },
        ask_reset(ev){
            this.to_reset = ev;
            this.reset_modal.show();
        },
        reset(){
            window.network.wso.send(JSON.stringify({ action: window.RPC.RESET_EVENT, params: this.to_reset.id }));
            this.reset_modal.hide();
        },
        remove(){
            window.network.wso.send(JSON.stringify({ action: window.RPC.REMOVE_SEASON, params: this.to_delete.id }));
            this.delete_modal.hide();
        }
    }
}
</script>

<style scoped>
.ev-icon{
    width: 48px;
    height: 40px;
    border-radius: 8px;
    background: var(--a-ink);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding-bottom: 5px;
    flex-shrink: 0;
}
</style>
